// Loyalty Phase 1 (Sep 2026) — recording a refund that was completed
// OUTSIDE Rudras-AI.
//
// Two channels, deliberately worded and gated differently.
//
// CASH is an ordinary counter operation: staff handed money back. Any
// tenant user may record it.
//
// CLOVER is an assertion about another processor's money. Rudras-AI
// cannot initiate a Clover refund and does not verify that one happened
// — `CloverTerminalProvider.refund` returns `unsupported`, honestly, and
// the engine now refuses to record anything from it. So this asks the
// owner to confirm they already completed the refund in Clover, and
// every surface labels the result "recorded externally" rather than
// implying Rudras-AI moved the money.
//
// The amount ceiling is enforced by the server against what was actually
// CAPTURED, re-read inside the same transaction that writes the record,
// so nothing here can authorise returning more than was collected.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const CHANNEL_CASH = "cash";
const CHANNEL_CLOVER = "clover_external";

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

// Durable, client-generated and minted ONCE per refund attempt. Resent
// unchanged on every retry so a double-click or a lost response records
// exactly one refund; the server's unique `(company_id, request_id)`
// index is what enforces it.
const newRequestId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `rr-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function RecordedRefundPanel({ orderId, refundable = true }) {
  // `refundable` is the CALLER's view of whether this payment is in a
  // state that can fund a refund at all (paid / partially refunded).
  // It is a courtesy that keeps an impossible control off the screen —
  // never the authority. The server decides, in
  // `services/recorded_refunds._authoritative_capture`, which refuses
  // any ledger row that is not demonstrably collected; `remaining`
  // below comes from that same computation, so a client that ignored
  // this prop entirely would still be refused.
  //
  // Sep 2026 (RUD-ORD-000004): this panel rendered unconditionally on
  // every order, and its remaining-refundable figure was computed
  // without reading payment status at all — so a PENDING card order
  // that had collected nothing offered staff a refund of its full
  // order total.
  // Ownership is resolved here rather than threaded in as a prop: this
  // panel is rendered from surfaces that do not otherwise need to know,
  // and the answer is authoritative on the server anyway — a non-owner
  // who somehow submitted a Clover record would still be refused by
  // `_require_owner`. This only decides whether to OFFER the option.
  const [isOwner, setIsOwner] = useState(false);
  const [existing, setExisting] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState(CHANNEL_CASH);
  const [requestId, setRequestId] = useState(newRequestId);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/commerce/orders/${orderId}/recorded-refunds`);
      setExisting(data?.items || []);
      setRemaining(Number(data?.remaining_cents || 0));
    } catch (e) {
      setError(e.response?.data?.detail?.message || "Failed to load refund records");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    api.get("/commerce/settings/loyalty")
      .then((r) => { if (alive) setIsOwner(!!r.data?.is_owner); })
      .catch(() => { /* best effort — defaults to hiding the Clover option */ });
    return () => { alive = false; };
  }, []);

  const reset = () => {
    setOpen(false);
    setAmount("");
    setReason("");
    setReference("");
    setConfirmed(false);
    setChannel(CHANNEL_CASH);
    setRequestId(newRequestId());
  };

  const submit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || Number.isNaN(cents) || cents <= 0) {
      toast.error("Enter a refund amount greater than zero");
      return;
    }
    if (cents > remaining) {
      toast.error(`Only ${money(remaining)} remains refundable on this order`);
      return;
    }
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    if (channel === CHANNEL_CLOVER && !confirmed) {
      toast.error("Confirm the refund was already completed in Clover");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/commerce/orders/${orderId}/recorded-refund`, {
        request_id: requestId,
        channel,
        amount_cents: cents,
        reason: reason.trim(),
        provider_reference: reference.trim() || undefined,
        externally_completed_confirmed: channel === CHANNEL_CLOVER ? confirmed : false,
      });
      toast.success(
        channel === CHANNEL_CASH
          ? `Cash refund of ${money(cents)} recorded`
          : `External Clover refund of ${money(cents)} recorded`,
      );
      reset();
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      if (detail.error === "refund_exceeds_captured") {
        toast.error(
          `That is more than remains refundable — ${money(detail.remaining_cents || 0)} left.`,
        );
        await load();
      } else {
        toast.error(detail.message || "Could not record the refund");
      }
    } finally {
      setBusy(false);
    }
  };

  const clover = channel === CHANNEL_CLOVER;

  return (
    <div className="border-t border-slate-200 pt-4 mt-4" data-testid="recorded-refund-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          Refunds recorded outside Rudras-AI
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs underline text-slate-500 disabled:opacity-40"
          data-testid="recorded-refund-refresh"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="py-3 text-sm text-slate-500" data-testid="recorded-refund-loading">
          Loading…
        </div>
      )}

      {!loading && error && (
        <div className="py-2" data-testid="recorded-refund-error">
          <div className="text-sm text-slate-600">{error}</div>
          <button
            type="button" onClick={load}
            className="mt-1 text-xs underline text-slate-600"
            data-testid="recorded-refund-retry"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          {(!refundable || remaining <= 0) ? (
            <div className="text-xs text-slate-600" data-testid="recorded-refund-not-refundable">
              Nothing is refundable on this order — no payment has been confirmed as
              collected.
            </div>
          ) : (
            <div className="text-xs text-slate-600" data-testid="recorded-refund-remaining">
              {money(remaining)} remains refundable against what was actually collected.
            </div>
          )}

          {existing.length > 0 && (
            <ul className="mt-2 divide-y text-xs" data-testid="recorded-refund-list">
              {existing.map((r) => (
                <li key={r.id} className="py-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {r.channel === CHANNEL_CASH ? "Cash refund" : "Clover refund"}
                      <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-normal text-slate-600">
                        Recorded externally
                      </span>
                    </div>
                    <div className="text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                    </div>
                    {r.reason && <div className="text-slate-600 italic">{r.reason}</div>}
                    {r.provider_reference && (
                      <div className="text-slate-500 font-mono">Ref {r.provider_reference}</div>
                    )}
                    {r.actor_email && <div className="text-slate-500">by {r.actor_email}</div>}
                  </div>
                  <div className="font-semibold shrink-0">{money(r.amount_cents)}</div>
                </li>
              ))}
            </ul>
          )}

          {refundable && remaining > 0 && (
            <div className="mt-3">
              {!open ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="text-xs font-semibold text-emerald-700 underline"
                  data-testid="recorded-refund-open"
                >
                  Record a refund
                </button>
              ) : (
                <div className="rounded border border-slate-200 p-3 space-y-2" data-testid="recorded-refund-form">
                  <fieldset>
                    <legend className="text-xs font-medium mb-1">How was it refunded?</legend>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio" name="rr-channel" value={CHANNEL_CASH}
                        checked={!clover}
                        onChange={() => { setChannel(CHANNEL_CASH); setConfirmed(false); }}
                        data-testid="recorded-refund-channel-cash"
                      />
                      <span>Cash handed back at the counter</span>
                    </label>
                    {isOwner && (
                      <label className="flex items-center gap-2 text-xs mt-1">
                        <input
                          type="radio" name="rr-channel" value={CHANNEL_CLOVER}
                          checked={clover}
                          onChange={() => setChannel(CHANNEL_CLOVER)}
                          data-testid="recorded-refund-channel-clover"
                        />
                        <span className="font-semibold">Record external Clover refund</span>
                      </label>
                    )}
                  </fieldset>

                  {clover && (
                    <div
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-2 text-xs text-amber-900"
                      data-testid="recorded-refund-clover-notice"
                    >
                      Rudras-AI does not initiate or verify this refund. You must
                      already have refunded the customer in Clover; this only records
                      that it happened, so the order's accounting and the member's
                      points match reality.
                      <label className="mt-1.5 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={confirmed}
                          onChange={(e) => setConfirmed(e.target.checked)}
                          data-testid="recorded-refund-clover-confirm"
                        />
                        <span>I have already completed this refund in Clover.</span>
                      </label>
                    </div>
                  )}

                  <label className="block text-xs font-medium" htmlFor="rr-amount">
                    Amount refunded ({money(remaining)} available)
                  </label>
                  <input
                    id="rr-amount" type="number" step="0.01" min="0"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-full rounded border px-2 py-1 text-sm"
                    data-testid="recorded-refund-amount"
                  />

                  <label className="block text-xs font-medium" htmlFor="rr-reason">
                    Reason (required)
                  </label>
                  <input
                    id="rr-reason" type="text"
                    value={reason} onChange={(e) => setReason(e.target.value)}
                    className="w-full rounded border px-2 py-1 text-sm"
                    data-testid="recorded-refund-reason"
                  />

                  {clover && (
                    <>
                      <label className="block text-xs font-medium" htmlFor="rr-reference">
                        Clover reference (optional)
                      </label>
                      <input
                        id="rr-reference" type="text"
                        value={reference} onChange={(e) => setReference(e.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                        data-testid="recorded-refund-reference"
                      />
                    </>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button" onClick={submit}
                      disabled={busy || (clover && !confirmed)}
                      className="rounded bg-emerald-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      data-testid="recorded-refund-submit"
                    >
                      {busy ? "Recording…" : "Record refund"}
                    </button>
                    <button
                      type="button" onClick={reset} disabled={busy}
                      className="text-xs underline text-slate-500"
                      data-testid="recorded-refund-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
